/**
 * The one place the application's locale is decided.
 *
 * Obsina is used in Turkish, so every date, time and number a member reads is
 * formatted for Turkish: `29 Tem 2026`, `18:15` on a 24-hour clock, `5.313`
 * with a full stop as the thousands separator. Before this module the string
 * `"en"` was written out at nineteen call sites — four of them hard-coded
 * inside a formatter, the rest inherited from a prop default — so the answer to
 * "what locale is this?" depended on which file you happened to open.
 *
 * It is a constant rather than a setting because there is nothing to choose
 * yet: the product ships one language. A future language picker replaces this
 * export with a read from wherever the choice is stored, and every formatter
 * follows without being touched again — which is the point of routing them all
 * through one name.
 *
 * `tr-TR` and not bare `tr`: the region decides the calendar, the clock and the
 * separators, and only the tagged form pins all three. Nothing here needs an
 * explicit `hourCycle` — `tr-TR` is a 24-hour locale by definition, and forcing
 * `h23` would be a second place to keep in step for no gain.
 */
export const APP_LOCALE = "tr-TR";

/**
 * The document language, kept beside the formatting locale because the two must
 * agree. It is the bare tag: `lang` describes the language of the text, not the
 * region's number formatting, and `apps/web/index.html` carries the same value
 * for the first paint before any script runs.
 */
export const APP_LANGUAGE = "tr";

/**
 * Turkish for the design system's own defaults.
 *
 * The package ships English strings for the controls it owns — a close button's
 * accessible name, the connection status pills, the table's region label — and
 * `ObsinaProvider` takes a `messages` prop precisely so an application can
 * replace them without the package having to know a second language. Passing
 * this is what keeps seventeen strings from printing English inside an
 * otherwise Turkish interface; nothing in `packages/design-system` changes.
 *
 * The status words are the design system's, but they describe the same
 * conditions the sync surface names, so they use the same Turkish the
 * application catalogue uses. A pill reading one word and the sentence beside
 * it another would be two names for one state.
 */
export const APP_DESIGN_SYSTEM_MESSAGES = {
  close: "Kapat",
  dismiss: "Yoksay",
  loading: "Yükleniyor",
  menu: "Diğer işlemler",
  dataTable: "Veri tablosu",
  toastRegion: "Bildirimler",
  progress: "İlerleme",
  removeTag: (label: string) => `${label} kaldır`,
  status: {
    synced: "Eşitlendi",
    syncing: "Eşitleniyor",
    error: "İlgi bekliyor",
    paused: "Duraklatıldı",
    offline: "Çevrimdışı",
    healthy: "Eşitlendi",
    partial: "Sorunlu eşitlendi",
    reauth: "Oturum doldu",
    disconnected: "Bağlı değil",
  },
} as const;

/**
 * The zone this product's own boundaries are expressed in.
 *
 * Obsina is used in Turkey, and a deadline read out of a message means the hour
 * a person in Turkey meant when they wrote it. The worker normalizes commitment
 * deadlines in `OBSINA_WORKER_TIME_ZONE`; this is the browser's half of that one
 * decision, and the two are only correct together. A history boundary picked as
 * "12 Haziran" is the instant that day begins here, not the instant it begins in
 * UTC three hours earlier.
 *
 * It is a constant for the same reason {@link APP_LOCALE} is: the product ships
 * one zone. A future per-tenant zone replaces this export with a read from
 * wherever that choice is stored, and every caller follows without being edited
 * again — which is why nothing else in the application writes the zone out.
 *
 * Every other date on the surface is still formatted with no `timeZone` at all
 * and renders in the reader's own. For the market this ships to those are the
 * same zone; the difference only appears for a member reading from elsewhere,
 * and pinning those is a separate change across every surface that prints a
 * date. It is not made here.
 */
export const APP_TIME_ZONE = "Europe/Istanbul";

/**
 * How {@link APP_TIME_ZONE} is named on screen, in the application's language.
 *
 * Derived from the zone rather than written out a second time: two places
 * stating the zone is two places to keep in step, and this one would be the one
 * that silently goes stale. Turkish resolves it to `TSİ`, the abbreviation a
 * reader here already knows.
 *
 * `shortGeneric` and not `short`: the generic form names the zone itself rather
 * than whichever offset is in force, so it cannot read as a seasonal claim.
 * Turkey has observed no daylight saving since 2016 and the two forms agree
 * year-round, but the generic one stays honest if that ever changes.
 */
export const APP_TIME_ZONE_LABEL = zoneLabel(APP_LOCALE, APP_TIME_ZONE);

function zoneLabel(locale: string, timeZone: string): string {
  // Read at the present moment, not at the epoch. A zone's name is a property
  // of the era you ask about: Turkey observed daylight saving until 2016, so
  // asking about 1970 answers `Türkiye Saati` — correct for 1970, and not what
  // anyone reading this screen calls the clock on their wall. The generic form
  // carries no offset, so within the era the product runs in this is stable
  // across the year and the same string every render.
  const named = new Intl.DateTimeFormat(locale, {
    timeZone,
    timeZoneName: "shortGeneric",
  })
    .formatToParts(new Date())
    .find((part) => part.type === "timeZoneName")?.value;
  // A runtime whose ICU cannot name the zone still says which zone it is,
  // rather than printing a date with no zone beside it at all.
  return named ?? timeZone;
}
